import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/shared/ThemeContext'
import apiService from '../../services/shared/apiService';

interface SymbolicOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  messageId: string;
  originalTags: string[];
  userId: string;
  onOverrideComplete?: () => void;
}

// Available symbolic tags for selection
const AVAILABLE_TAGS = [
  // Emotional states
  'joy', 'sadness', 'anger', 'anxiety', 'calmness', 'confusion',
  // Activity and energy
  'fatigue', 'energy', 'inertia', 'motivation',
  // Social interaction
  'isolation', 'connection', 'conflict', 'lonely',
  // Cognitive patterns
  'focus', 'scatter', 'rumination', 'clarity',
  // Self-perception
  'shame', 'confidence', 'identity', 'worth',
  // Sleep and rest
  'sleep', 'rest', 'insomnia', 'refreshed',
  // Growth and healing
  'healing', 'growth', 'gratitude', 'learning',
  // Risk assessment (only for PG/FULL modes)
  'suicide_risk', 'self_harm',
  // Additional patterns
  'overwhelm', 'stress', 'peace', 'tension', 'relief'
];

const SymbolicOverrideModal: React.FC<SymbolicOverrideModalProps> = ({
  isOpen,
  onClose,
  messageId,
  originalTags,
  userId,
  onOverrideComplete
}) => {
  const { themeClasses } = useTheme();
  const [selectedTags, setSelectedTags] = useState<string[]>(originalTags);
  const [customTag, setCustomTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedTags(originalTags);
      setCustomTag('');
      setError(null);
    }
  }, [isOpen, originalTags]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleAddCustomTag = () => {
    if (customTag.trim() && !selectedTags.includes(customTag.trim())) {
      setSelectedTags(prev => [...prev, customTag.trim()]);
      setCustomTag('');
    }
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      setError('Please select at least one tag or add a custom tag.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiService.submitSymbolicOverride({
        userId,
        messageId,
        originalTags,
        correctedTags: selectedTags,
        timestamp: new Date().toISOString()
      });

      onOverrideComplete?.();
      onClose();
    } catch (err) {
      console.error('Error submitting symbolic override:', err);
      setError('Failed to save your correction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${themeClasses.cardBg} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`${themeClasses.cardBg} border-b border-gray-200 p-6`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${themeClasses.primaryText}`}>
              Adjust Symbolic Tags
            </h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100  ${themeClasses.primaryText}`}
            >
              ✕
            </button>
          </div>
          <p className={`text-sm mt-2 ${themeClasses.secondaryText}`}>
            Select the tags that better represent how you were feeling in this message.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Original Tags */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${themeClasses.primaryText}`}>
              Original Tags:
            </h3>
            <div className="flex flex-wrap gap-2">
              {originalTags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-200  text-gray-700  text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
              {originalTags.length === 0 && (
                <span className="text-gray-500  text-sm italic">
                  No tags were originally assigned
                </span>
              )}
            </div>
          </div>

          {/* Tag Selection */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${themeClasses.primaryText}`}>
              Select Tags:
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {AVAILABLE_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : `${themeClasses.input} border-gray-300  ${themeClasses.primaryText} hover:bg-gray-50 `
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Tag */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${themeClasses.primaryText}`}>
              Add Custom Tag:
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={customTag}
                onChange={(e) => setCustomTag(e.target.value)}
                placeholder="Enter a custom tag..."
                className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.input} ${themeClasses.primaryText}`}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
              />
              <button
                onClick={handleAddCustomTag}
                disabled={!customTag.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>

          {/* Selected Tags Preview */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${themeClasses.primaryText}`}>
              Your Selected Tags:
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedTags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-blue-100  text-blue-800  text-sm rounded-full flex items-center gap-1"
                >
                  {tag}
                  <button
                    onClick={() => handleTagToggle(tag)}
                    className="text-blue-600  hover:text-blue-800 "
                  >
                    ✕
                  </button>
                </span>
              ))}
              {selectedTags.length === 0 && (
                <span className="text-gray-500  text-sm italic">
                  No tags selected
                </span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100  text-red-700  rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${themeClasses.cardBg} border-t border-gray-200  p-6 flex gap-3 justify-end`}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-lg border ${themeClasses.input} ${themeClasses.primaryText} hover:bg-gray-50  disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedTags.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Correction'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SymbolicOverrideModal; 